import React, { useMemo, useState, useEffect } from 'react';
import {
  Button, Table, Badge, Modal, Form, Alert, Spinner, Stack
} from 'react-bootstrap';

import { 
  createTenantProject, 
  cloneTaxonomyStandard,
  getTenantTaxonomies 
} from '../../api/api';
import ProjectAssignmentModal from './ProjectAssignmentModal';
import { showToast } from '../../utils/toast';

const ProjectTable = ({ projects = [], programs = [], tenantSlug, onRefresh }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  
  const [taxonomies, setTaxonomies] = useState([]);
  const [selectedTaxonomyDetails, setSelectedTaxonomyDetails] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [cloning, setCloning] = useState(false);

  const [form, setForm] = useState({ name: '', program_id: '', taxonomy_id: '' });
  const [editTaxForm, setEditTaxForm] = useState({ name: '', levels: {}, verbs: {} });

  const [showAssign, setShowAssign] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const loadTaxonomies = async () => {
    try {
      const res = await getTenantTaxonomies(tenantSlug);
      setTaxonomies(res.data || []);
    } catch (e) { 
      console.error("Failed to load taxonomies", e); 
    }
  };

  useEffect(() => {
    if (tenantSlug) loadTaxonomies();
  }, [tenantSlug]);

  const activePrograms = useMemo(() => programs.filter((p) => (p.status || 'active') === 'active'), [programs]);

  const handleInspect = (id) => {
    const tax = taxonomies.find(t => String(t.id) === String(id));
    if (!tax) return;
    setSelectedTaxonomyDetails(tax);
    setEditTaxForm({
      name: `${tax.name} (Custom)`,
      levels: { ...tax.levels_config },
      verbs: { ...tax.verbs_config }
    });
    setIsEditing(false);
  };

  const handleSaveClone = async () => {
    try {
      setCloning(true);
      setError('');
      const payload = {
        name: editTaxForm.name,
        levels_config: editTaxForm.levels,
        verbs_config: editTaxForm.verbs,
        description: selectedTaxonomyDetails.description,
        reference_policy: selectedTaxonomyDetails.reference_policy
      };
      
      const res = await cloneTaxonomyStandard(tenantSlug, selectedTaxonomyDetails.id, payload);
      setTaxonomies(prev => [...prev, res.data]);
      setForm(prev => ({ ...prev, taxonomy_id: res.data.id.toString() }));
      setSelectedTaxonomyDetails(null);
      setIsEditing(false);
      showToast("Custom standard created and selected!", 'warning');
    } catch (e) {
      setError("Failed to save custom taxonomy.");
    } finally {
      setCloning(false);
    }
  };

  const handleCreateProject = async () => {
    if (!form.name.trim() || !form.program_id || !form.taxonomy_id) {
      setError('Missing required fields'); return;
    }
    try {
      setCreating(true);
      await createTenantProject(tenantSlug, {
        name: form.name.trim(),
        program_id: Number(form.program_id),
        taxonomy_id: Number(form.taxonomy_id)
      });
      setShowCreate(false);
      setForm({ name: '', program_id: '', taxonomy_id: '' });
      onRefresh?.();
    } catch (e) { setError('Failed to create project.'); } 
    finally { setCreating(false); }
  };

  const renderHierarchy = (config) => {
    if (!config) return "Not defined";
    const levels = Object.values(config).sort((a, b) => a.score - b.score);
    return levels.map(l => l.name).join(' → ');
  };

  return (
    <>
      <div className="d-flex justify-content-between mb-3 pt-4 border-top">
        <div>
          <strong className="fs-5">Project Registry</strong>
          <p className="text-muted small mb-0">Master list of all production pipelines in the tenant.</p>
        </div>
        <Button size="sm" variant="primary" onClick={() => setShowCreate(true)} disabled={activePrograms.length === 0}>
          + Create New Project
        </Button>
      </div>

      <Table bordered hover responsive size="sm" className="mb-0 align-middle shadow-sm">
        <thead className="table-dark small text-uppercase">
          <tr>
            <th scope="col" className="ps-3">Project Name</th>
            <th>Program</th>
            <th>Assigned SME</th>
            <th>Current Stage</th>
            <th scope="col" className="text-end pe-3">Management</th>
          </tr>
        </thead>
        <tbody>
          {projects.map(p => {
            // 1. Program Name is now safely provided by the updated backend payload
            // const progName = p.program?.name || "N/A";
            
            // 2. Safely find the SME from the newly attached p.assignments array (case-insensitive)
            // const sme = p.assignments?.find(a => String(a.role || '').toLowerCase() === 'sme');
            
            // 3. Fallback logic: prefer name, then email, else "Unassigned"
            // const smeDisplay = sme ? (sme.user_name || sme.user_email) : "Unassigned";
            
            return (
              <tr key={p.id}>
                <td className="ps-3 fw-bold text-primary">{p.name}</td>
                <td className="fw-medium">{p.program_name || "N/A"}</td>
                <td>
                  <small className="text-muted fw-bold">
                    {p.sme_name ? (
                        <Badge bg="info" className="bg-opacity-10 text-info border border-info">
                            {p.sme_name}
                        </Badge>
                    ) : (
                        <span className="text-muted fw-normal">Unassigned</span>
                    )}
                  </small>
                </td>
                <td>
                  <Badge bg="light" text="dark" className="border text-capitalize">
                    {(p.current_stage || "Pending").replace('_', ' ')}
                  </Badge>
                </td>
                <td className="text-end pe-3">
                  <Button 
                    size="sm" 
                    variant="outline-primary" 
                    onClick={() => { setSelectedProject(p); setShowAssign(true); }}
                  >
                    Assign Team
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Main Creation Modal */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton><Modal.Title className="fw-bold">Create Production Project</Modal.Title></Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" role="alert">{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold">Project Name</Form.Label>
            <Form.Control value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold">Academic Program</Form.Label>
            <Form.Select value={form.program_id} onChange={e => setForm({...form, program_id: e.target.value})}>
              <option value="">Select Program...</option>
              {activePrograms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold">Taxonomy Standard</Form.Label>
            <Stack direction="horizontal" gap={2}>
              <Form.Select value={form.taxonomy_id} onChange={e => setForm({...form, taxonomy_id: e.target.value})}>
                <option value="">Select Taxonomy...</option>
                {taxonomies.map((t) => ( <option key={t.id} value={t.id}>{t.name} {t.tenant_id ? '(Custom)':'(Global)'}</option>))}
              </Form.Select>
              <Button variant="outline-info" onClick={() => handleInspect(form.taxonomy_id)} disabled={!form.taxonomy_id}>Inspect</Button>
            </Stack>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="primary" onClick={handleCreateProject} disabled={creating}>Create Project</Button>
        </Modal.Footer>
      </Modal>

      {/* Taxonomy Inspection Modal */}
      <Modal show={!!selectedTaxonomyDetails} onHide={() => setSelectedTaxonomyDetails(null)} size="lg">
        <Modal.Header closeButton><Modal.Title>{isEditing ? 'Clone & Modify Taxonomy' : 'Inspect Standard'}</Modal.Title></Modal.Header>
        <Modal.Body>
          {!isEditing ? (
            <>
              <h6>Hierarchy: <span className="text-primary">{renderHierarchy(selectedTaxonomyDetails?.levels_config)}</span></h6>
              <hr />
              <Table striped bordered size="sm">
                <thead><tr><th>Level</th><th>Action Verbs</th></tr></thead>
                <tbody>
                  {selectedTaxonomyDetails?.verbs_config && Object.entries(selectedTaxonomyDetails.verbs_config).map(([k, v]) => (
                    <tr key={k}>
                      <td>{selectedTaxonomyDetails.levels_config[k]?.name}</td>
                      <td><small className="text-muted">{v.join(', ')}</small></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          ) : (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">New Taxonomy Name</Form.Label>
                <Form.Control value={editTaxForm.name} onChange={e => setEditTaxForm({...editTaxForm, name: e.target.value})} />
              </Form.Group>
              <h6>Adjust Level Titles</h6>
              {Object.keys(editTaxForm.levels).map(key => (
                <Form.Group key={key} className="mb-2 d-flex align-items-center gap-2">
                  <Badge bg="dark">Level {key}</Badge>
                  <Form.Control 
                    value={editTaxForm.levels[key].name} 
                    onChange={e => {
                      const newLevels = {...editTaxForm.levels}; newLevels[key].name = e.target.value; setEditTaxForm({...editTaxForm, levels: newLevels});
                    }}
                  />
                </Form.Group>
              ))}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          {!isEditing ? (
            <Button variant="warning" onClick={() => setIsEditing(true)}>Clone & Customize</Button>
          ) : (
            <Button variant="success" onClick={handleSaveClone} disabled={cloning}>
              {cloning ? <Spinner size="sm" aria-hidden="true" /> : 'Save Custom Standard'}
            </Button>
          )}
          <Button variant="secondary" onClick={() => setSelectedTaxonomyDetails(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {selectedProject && (
        <ProjectAssignmentModal 
          show={showAssign} 
          onHide={() => setShowAssign(false)} 
          tenantSlug={tenantSlug} 
          project={selectedProject} 
        />
      )}
    </>
  );
};

export default ProjectTable;